import { ClientInfo, PortfolioAnalysis, Holding } from '@/types/portfolio';
import { PortfolioAssumptions } from '@/lib/assumptions';
import { ScoringConfig, AdviceModel } from '@/lib/scoring-config';
import { MetricCard } from './MetricCard';
import { SettingsPanel } from './SettingsPanel';
import { PartnerAccountSwitcher, PartnerInvitationNotifications } from './partner';
import { Partner } from '@/hooks/usePartner';
import { Button } from '@/components/ui/button';
import { OutlierLogo } from '@/components/ui/OutlierLogo';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FileDown, ChevronDown, Database, Eye, TrendingUp, Table, GraduationCap, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

type ViewMode = 'individual' | 'partner' | 'household';

// Model tab metrics interface
export interface ModelMetrics {
  userPortfolio: { value: number; totalReturn: number };
  benchmark: { value: number; totalReturn: number };
  tactical: { value: number; totalReturn: number; name: string };
}

interface HeaderProps {
  clientInfo: ClientInfo;
  analysis: PortfolioAnalysis;
  holdings: Holding[];
  notes: string;
  assumptions: PortfolioAssumptions;
  scoringConfig: ScoringConfig;
  adviceModel: AdviceModel;
  advisorFee: number;
  partner: Partner | null;
  partnerView: ViewMode;
  onPartnerViewChange: (view: ViewMode) => void;
  onClientInfoChange: (info: ClientInfo) => void;
  onAssumptionsChange: (assumptions: PortfolioAssumptions) => void;
  onScoringConfigChange: (config: ScoringConfig) => void;
  onAdviceModelChange: (model: AdviceModel) => void;
  onAdvisorFeeChange: (fee: number) => void;
  onLoadSample: () => void;
  // Tab navigation props
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  // Model metrics for when on Model tab
  modelMetrics?: ModelMetrics | null;
}

export function Header({
  clientInfo,
  analysis,
  holdings,
  notes,
  assumptions,
  scoringConfig,
  adviceModel,
  advisorFee,
  partner,
  partnerView,
  onPartnerViewChange,
  onClientInfoChange: _onClientInfoChange,
  onAssumptionsChange,
  onScoringConfigChange,
  onAdviceModelChange,
  onAdvisorFeeChange,
  onLoadSample,
  activeTab = 'observations',
  onTabChange,
  modelMetrics,
}: HeaderProps) {

  const handleExport = async (type: 'full' | 'summary' | 'recommendations') => {
    try {
      // Dynamically import PDF export to reduce initial bundle size
      const { generatePDF } = await import('@/lib/pdf-export');
      await generatePDF(analysis, holdings, clientInfo, notes, type);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report exported`);
    } catch (error) {
      toast.error('Failed to export PDF');
      console.error(error);
    }
  };

  const { user } = useAuth();

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
      <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
        {/* Top row - Logo, Household Selector, Centered Tabs, Actions */}
        <div className="relative flex items-center justify-between gap-3 mb-3 sm:mb-4">
          {/* Left side - Logo and Partner Switcher */}
          <div className="flex items-center gap-4 shrink-0">
            <OutlierLogo size="md" />
            {/* Partner Account Switcher */}
            <PartnerAccountSwitcher
              currentUser={{ name: user?.email?.split('@')[0] || 'You' }}
              partner={partner}
              currentView={partnerView}
              onViewChange={onPartnerViewChange}
            />
          </div>

          {/* Center - Tab navigation (responsive) */}
          {onTabChange && (
            <TooltipProvider delayDuration={300}>
              <div className="hidden md:inline-flex h-9 items-center justify-center rounded-lg bg-muted/50 p-1 text-muted-foreground absolute left-1/2 -translate-x-1/2">
                {/* Icon-only at md, with labels at lg+ */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onTabChange('observations')}
                      className={cn(
                        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 lg:px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 gap-1.5',
                        activeTab === 'observations' ? 'bg-background text-foreground shadow' : 'hover:bg-background/50'
                      )}
                    >
                      <Eye size={16} />
                      <span className="hidden lg:inline">Observations</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">Observations</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onTabChange('model')}
                      className={cn(
                        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 lg:px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 gap-1.5',
                        activeTab === 'model' ? 'bg-background text-foreground shadow' : 'hover:bg-background/50'
                      )}
                    >
                      <TrendingUp size={16} />
                      <span className="hidden lg:inline">Model</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">Model</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onTabChange('holdings')}
                      className={cn(
                        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 lg:px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 gap-1.5',
                        activeTab === 'holdings' ? 'bg-background text-foreground shadow' : 'hover:bg-background/50'
                      )}
                    >
                      <Table size={16} />
                      <span className="hidden lg:inline">Holdings</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">Holdings</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onTabChange('learn')}
                      className={cn(
                        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 lg:px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 gap-1.5',
                        activeTab === 'learn' ? 'bg-background text-foreground shadow' : 'hover:bg-background/50'
                      )}
                    >
                      <GraduationCap size={16} />
                      <span className="hidden lg:inline">Learn</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="lg:hidden">Learn</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}

          {/* Right side - Actions */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Partner Invitation Notifications */}
            {user && <PartnerInvitationNotifications />}

            {/* Compact actions dropdown for smaller screens */}
            <div className="hidden lg:flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onLoadSample} className="gap-1.5 text-xs" aria-label="Load sample portfolio data">
                <Database size={14} aria-hidden="true" />
                <span className="hidden xl:inline">Sample</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs" aria-label="Export portfolio as PDF">
                    <FileDown size={14} aria-hidden="true" />
                    <span className="hidden xl:inline">Export</span> PDF
                    <ChevronDown size={12} aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover">
                  <DropdownMenuItem onClick={() => handleExport('full')}>
                    Full Report (2-3 pages)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('summary')}>
                    Executive Summary (1 page)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('recommendations')}>
                    Recommendations Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Collapsed menu for md-lg screens */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden" aria-label="More actions">
                  <Menu size={16} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover w-48">
                <DropdownMenuItem onClick={onLoadSample}>
                  <Database size={14} className="mr-2" />
                  Load Sample Data
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport('full')}>
                  <FileDown size={14} className="mr-2" />
                  Export Full Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('summary')}>
                  <FileDown size={14} className="mr-2" />
                  Export Summary
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('recommendations')}>
                  <FileDown size={14} className="mr-2" />
                  Export Recommendations
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <SettingsPanel
              assumptions={assumptions}
              onUpdate={onAssumptionsChange}
              scoringConfig={scoringConfig}
              onScoringConfigUpdate={onScoringConfigChange}
              adviceModel={adviceModel}
              onAdviceModelChange={onAdviceModelChange}
              advisorFee={advisorFee}
              onAdvisorFeeChange={onAdvisorFeeChange}
              currentRiskTolerance={clientInfo.riskTolerance}
            />
          </div>
        </div>

        {/* Metrics row - responsive scroll on mobile */}
        <div className="pt-2 sm:pt-3 border-t border-border/50 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <div className="flex items-center gap-4 sm:gap-6 min-w-max sm:min-w-0 sm:flex-wrap">
            {/* Conditional metrics based on active tab */}
            {activeTab === 'model' && modelMetrics ? (
              (() => {
                // Calculate performance rankings (1 = best, 3 = worst)
                const values = [
                  { key: 'user', value: modelMetrics.userPortfolio.value },
                  { key: 'benchmark', value: modelMetrics.benchmark.value },
                  { key: 'tactical', value: modelMetrics.tactical.value },
                ];
                const sorted = [...values].sort((a, b) => b.value - a.value);
                const getRank = (key: string): 1 | 2 | 3 => {
                  const index = sorted.findIndex(v => v.key === key);
                  return (index + 1) as 1 | 2 | 3;
                };

                return (
                  <>
                    <MetricCard
                      label="Your Portfolio"
                      value={`$${modelMetrics.userPortfolio.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                      subValue={`${modelMetrics.userPortfolio.totalReturn >= 0 ? '+' : ''}${(modelMetrics.userPortfolio.totalReturn * 100).toFixed(1)}%`}
                      tooltip="Your portfolio's projected/historical value"
                      performanceRank={getRank('user')}
                    />
                    <MetricCard
                      label="S&P 500"
                      value={`$${modelMetrics.benchmark.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                      subValue={`${modelMetrics.benchmark.totalReturn >= 0 ? '+' : ''}${(modelMetrics.benchmark.totalReturn * 100).toFixed(1)}%`}
                      tooltip="S&P 500 benchmark comparison"
                      performanceRank={getRank('benchmark')}
                    />
                    <MetricCard
                      label={modelMetrics.tactical.name}
                      value={`$${modelMetrics.tactical.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                      subValue={`${modelMetrics.tactical.totalReturn >= 0 ? '+' : ''}${(modelMetrics.tactical.totalReturn * 100).toFixed(1)}%`}
                      tooltip="Tactical fund comparison"
                      performanceRank={getRank('tactical')}
                    />
                  </>
                );
              })()
            ) : (
              <>
                <MetricCard
                  label="Portfolio Value"
                  value={`$${analysis.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  tooltip="Total market value of all holdings"
                />
                <MetricCard
                  label="Expected Return"
                  value={`${(analysis.expectedReturn * 100).toFixed(1)}%`}
                  tooltip="Weighted average expected annual return"
                  trend={analysis.expectedReturn > 0.06 ? 'up' : 'neutral'}
                />
                <MetricCard
                  label="Volatility"
                  value={`${(analysis.volatility * 100).toFixed(1)}%`}
                  tooltip="Expected annual standard deviation"
                />
                <MetricCard
                  label="Sharpe Ratio"
                  value={analysis.sharpeRatio.toFixed(2)}
                  tooltip={`Risk-adjusted return metric. Target: ${scoringConfig.sharpe.portfolioTarget.toFixed(2)}`}
                  trend={analysis.sharpeRatio >= scoringConfig.sharpe.portfolioTarget ? 'up' : analysis.sharpeRatio < 0.3 ? 'down' : 'neutral'}
                />
                <MetricCard
                  label="Total Fees"
                  value={`$${analysis.totalFees.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  subValue={`${((analysis.totalFees / (analysis.totalValue || 1)) * 100).toFixed(2)}%/yr`}
                  tooltip="Annual expense ratio fees"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
